import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  Business,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { Link as MuiLink } from '@mui/material';
import { authAPI, getAuthMode } from '../services/api';

// SSO Provider icons
const SSOIcons: Record<string, string> = {
  azure_ad: 'https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg',
  okta: 'https://www.okta.com/sites/default/files/Okta_Logo_BrightBlue_Medium.png',
  google: 'https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png',
  saml_generic: '', // Will use default icon
};

// SSO Provider labels
const SSOLabels: Record<string, string> = {
  azure_ad: 'Microsoft Azure AD',
  okta: 'Okta',
  google: 'Google Workspace',
  saml_generic: 'Enterprise SSO',
};

interface SSOConfig {
  providers: string[];
  passwordEnabled: boolean;
  hasSSO: boolean;
  organizationId?: number;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // SSO state
  const [ssoConfig, setSsoConfig] = useState<SSOConfig | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(true);
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  // Keycloak/OIDC — a separate mechanism from the domain-based SSO above. Runtime-gated: the
  // default (unreached-endpoint or flag-off) is `false`, so the button below never renders unless
  // the backend explicitly says OIDC is on.
  const [oidcAvailable, setOidcAvailable] = useState(false);
  // PRISM_PASSWORD_LOGIN=false (the appliance with Entra): the backend 404s password and demo
  // login, so the page offers single sign-on only. Defaults to true — see getAuthMode.
  const [passwordAvailable, setPasswordAvailable] = useState(true);

  useEffect(() => {
    getAuthMode().then((m) => {
      setOidcAvailable(m.oidc);
      setPasswordAvailable(m.password);
    });
  }, []);

  // OTP state — when SES is configured, /auth/login returns step:'otp_sent'
  // and the user must enter the 6-digit code from their email before the JWT
  // is issued via /auth/login/verify.
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [otpCode, setOtpCode] = useState('');
  const [otpRecipientHint, setOtpRecipientHint] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const startResendCooldown = React.useCallback(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Check for SSO callback (token in URL)
  useEffect(() => {
    const token = searchParams.get('token');
    const role = searchParams.get('role');
    const orgId = searchParams.get('organizationId');
    const errorMsg = searchParams.get('error');

    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      // Clean URL
      window.history.replaceState({}, document.title, '/login');
      return;
    }

    if (token) {
      // SSO login successful - store token and redirect
      localStorage.setItem('token', token);
      if (role) localStorage.setItem('userRole', role);
      if (orgId) localStorage.setItem('organizationId', orgId);
      setSuccess('SSO login successful! Redirecting...');

      // Clean URL and redirect
      window.history.replaceState({}, document.title, '/login');
      setTimeout(() => navigate('/dashboard'), 1000);
    }
  }, [searchParams, navigate]);

  // Check for SSO config when email domain is entered
  useEffect(() => {
    const checkSSOConfig = async () => {
      if (!email.includes('@')) {
        setSsoConfig(null);
        setShowPasswordLogin(true);
        return;
      }

      const domain = email.split('@')[1];
      if (!domain || domain.length < 3) return;

      setSsoLoading(true);
      try {
        const API_URL = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${API_URL}/auth/sso/config/domain/${domain}`);
        if (response.ok) {
          const config = await response.json();
          setSsoConfig(config);
          setOrganizationId(config.organizationId || null);
          // Only hide password login if SSO is enforced
          setShowPasswordLogin(config.passwordEnabled);
        }
      } catch (err) {
        console.log('No SSO config for domain');
      } finally {
        setSsoLoading(false);
      }
    };

    const debounce = setTimeout(checkSSOConfig, 500);
    return () => clearTimeout(debounce);
  }, [email]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authAPI.login(email, password);
      // OTP-gated path: server sent a code, advance to step 2.
      if (result?.step === 'otp_sent') {
        setOtpRecipientHint(result.otp_recipient_hint || '');
        setStep('otp');
        startResendCooldown();
        return;
      }
      // Direct path (local dev with no SES): token already stored.
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.loginVerifyOtp(email, otpCode);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      const result = await authAPI.login(email, password);
      if (result?.step === 'otp_sent') {
        setOtpRecipientHint(result.otp_recipient_hint || otpRecipientHint);
        startResendCooldown();
      } else {
        setError('Unable to resend code.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to resend code.');
    } finally {
      setLoading(false);
    }
  };

  // Keycloak/Entra OIDC — a top-level navigation, so the backend can redirect to the IdP.
  const startOidcLogin = () => {
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    window.location.href = `${API_URL}/auth/oidc/start`;
  };

  const handleSSOLogin = (provider: string) => {
    if (!organizationId) {
      setError('Organization not found. Please enter your work email first.');
      return;
    }

    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const redirectUri = encodeURIComponent('/dashboard');
    window.location.href = `${API_URL}/auth/sso/login/${provider}?organization_id=${organizationId}&redirect_uri=${redirectUri}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a192f 0%, #112240 50%, #0a192f 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0,217,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(100,255,218,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 5,
          width: '100%',
          maxWidth: 450,
          borderRadius: 3,
          position: 'relative',
          zIndex: 1,
          background: 'rgba(23, 42, 69, 0.8) !important',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 217, 255, 0.15) !important',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box component="h1" sx={{ m: 0, mb: 2, lineHeight: 1 }}>
            <img
              src="/risk-aperture-logo.svg"
              alt="Risk Aperture"
              style={{
                height: 80,
                width: 'auto',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Box>
          <Typography variant="subtitle1" sx={{ color: '#a8b2cc' }}>
            Protective Risk Intelligence &amp; Security Modeling
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#7a8aaa', mt: 0.5, letterSpacing: '0.15em', fontWeight: 600 }}>
            (PRISM)
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {step === 'otp' ? (
          <form onSubmit={handleVerifyOtp}>
            <Typography variant="body2" sx={{ color: '#a8b2cc', mb: 2, textAlign: 'center' }}>
              We sent a 6-digit verification code{' '}
              {otpRecipientHint && (
                <>to <strong style={{ color: '#64ffda' }}>{otpRecipientHint}</strong></>
              )}
              . Enter it below to finish signing in.
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={otpCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpCode(val);
              }}
              margin="normal"
              required
              autoFocus
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: 24, letterSpacing: 8, fontFamily: 'monospace' },
              }}
              helperText="Enter the 6-digit code from your email"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otpCode.length !== 6}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <MuiLink
                component="button"
                type="button"
                sx={{ color: resendCooldown > 0 ? '#7a8aaa' : '#00d9ff', fontSize: 13 }}
                onClick={handleResendOtp}
                disabled={resendCooldown > 0 || loading}
              >
                {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
              </MuiLink>
              <MuiLink
                component="button"
                type="button"
                sx={{ color: '#7a8aaa', fontSize: 13 }}
                onClick={() => { setStep('password'); setOtpCode(''); setError(''); }}
              >
                Back to sign in
              </MuiLink>
            </Box>
          </form>
        ) : (
          <>
        {/* SSO Buttons - show when SSO is available */}
        {ssoConfig?.hasSSO && ssoConfig.providers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Sign in with your organization
            </Typography>

            {ssoConfig.providers.map((provider) => (
              <Button
                key={provider}
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => handleSSOLogin(provider)}
                disabled={loading}
                sx={{
                  mb: 1,
                  py: 1.5,
                  justifyContent: 'flex-start',
                  pl: 3,
                  textTransform: 'none',
                }}
                startIcon={
                  SSOIcons[provider] ? (
                    <img
                      src={SSOIcons[provider]}
                      alt={provider}
                      style={{ width: 24, height: 24 }}
                    />
                  ) : (
                    <Business />
                  )
                }
              >
                Continue with {SSOLabels[provider] || provider}
              </Button>
            ))}

            {/* Show password option toggle if password login is allowed */}
            {ssoConfig.passwordEnabled && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    or
                  </Typography>
                </Divider>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => setShowPasswordLogin(!showPasswordLogin)}
                  endIcon={showPasswordLogin ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  sx={{ textTransform: 'none' }}
                >
                  {showPasswordLogin ? 'Hide' : 'Show'} password login
                </Button>
              </>
            )}
          </Box>
        )}

        {!passwordAvailable && oidcAvailable && (
          <Button
            type="button"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            onClick={startOidcLogin}
            sx={{ mt: 1, py: 1.5, textTransform: 'none' }}
          >
            Sign in with single sign-on
          </Button>
        )}

        {/* Password Login Form */}
        {passwordAvailable && (
        <Collapse in={showPasswordLogin || !ssoConfig?.hasSSO}>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
                endAdornment: ssoLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              helperText={
                ssoConfig?.hasSSO && !ssoConfig?.passwordEnabled
                  ? 'SSO is required for your organization'
                  : undefined
              }
            />

            {(showPasswordLogin || !ssoConfig?.hasSSO) && (
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {(showPasswordLogin || !ssoConfig?.hasSSO) && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email || !password || (ssoConfig?.hasSSO && !ssoConfig?.passwordEnabled)}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            )}

            {oidcAvailable && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    or
                  </Typography>
                </Divider>
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  size="large"
                  disabled={loading}
                  onClick={startOidcLogin}
                  sx={{ py: 1.5, textTransform: 'none' }}
                >
                  Sign in with single sign-on
                </Button>
              </>
            )}
          </form>
        </Collapse>
        )}

        {passwordAvailable && (
          <>
            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" sx={{ textAlign: 'center', color: '#7a8aaa' }}>
              Want to explore first?{' '}
              <Typography
                component="a"
                href="/demo"
                variant="body2"
                sx={{ color: '#00d9ff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Try the self-guided demo
              </Typography>
            </Typography>
          </>
        )}
          </>
        )}

      </Paper>
    </Box>
  );
};

export default Login;
